import React from 'react';

import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import type { RoomRateRoom } from './utils';

// A real rate plan (post-Submission) or a rate plan draft, whichever the CM is currently
// choosing among -- only the id/name pair matters for rendering and selection.
export type RoomRateRatePlanOption = { id: string; name: string };

interface Props {
  appliedRatePlanNamesByRoomTypeId: Record<string, Array<string>>;
  disabled: boolean;
  onSelectRatePlans: (roomTypeId: string, ratePlanIds: Array<string>) => void;
  ratePlanOptions: Array<RoomRateRatePlanOption>;
  readOnly: boolean;
  room: RoomRateRoom;
  selectedRatePlanIds: Record<string, Array<string>>;
}

export function getReadOnlyRatePlanLabels(
  roomTypeId: string,
  appliedRatePlanNamesByRoomTypeId: Record<string, Array<string>>,
  ratePlanOptions: Array<RoomRateRatePlanOption>,
  selectedRatePlanIds: Record<string, Array<string>>,
) {
  const appliedNames = appliedRatePlanNamesByRoomTypeId[roomTypeId];

  if (appliedNames?.length) {
    return appliedNames;
  }

  const selectedRatePlanIdsForRoom = selectedRatePlanIds[roomTypeId] ?? [];

  return ratePlanOptions
    .filter((ratePlan) => selectedRatePlanIdsForRoom.includes(ratePlan.id))
    .map((ratePlan) => ratePlan.name || ratePlan.id);
}

export default function RoomRateRoomRow({
  appliedRatePlanNamesByRoomTypeId,
  disabled,
  onSelectRatePlans,
  ratePlanOptions,
  readOnly,
  room,
  selectedRatePlanIds,
}: Props) {
  const readOnlyRatePlanLabels = getReadOnlyRatePlanLabels(
    room.id,
    appliedRatePlanNamesByRoomTypeId,
    ratePlanOptions,
    selectedRatePlanIds,
  );

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          width: '100%',
          minHeight: 64,
          px: 2,
          py: 1,
          borderLeft: 3,
          borderColor:
            readOnly || selectedRatePlanIds[room.id]?.length || room.hasRatePlan ? 'primary.main' : 'transparent',
          bgcolor: 'background.paper',
          '&:hover': {
            bgcolor: room.hasRatePlan || disabled || readOnly ? undefined : 'action.hover',
          },
        }}
      >
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2">{room.name}</Typography>
          {room.hasRatePlan && !readOnly && (
            <Typography variant="caption" color="text.secondary">
              All rate plans already applied
            </Typography>
          )}
        </Stack>
        {readOnly ? (
          <Stack direction="row" flexWrap="wrap" gap={0.5} justifyContent="flex-end" sx={{ maxWidth: '60%' }}>
            {readOnlyRatePlanLabels.length > 0 ? (
              readOnlyRatePlanLabels.map((label) => <Chip key={label} label={label} size="small" variant="outlined" />)
            ) : (
              <Typography variant="caption" color="text.secondary">
                No rate plans applied
              </Typography>
            )}
          </Stack>
        ) : (
          !room.hasRatePlan && (
            <FormControl size="small" sx={{ width: 280, flexShrink: 0 }}>
              <InputLabel id={`flash-room-rate-plan-${room.id}`}>Rate plan</InputLabel>
              <Select
                label="Rate plan"
                labelId={`flash-room-rate-plan-${room.id}`}
                multiple
                value={selectedRatePlanIds[room.id] ?? []}
                disabled={disabled}
                renderValue={(selected) =>
                  ratePlanOptions
                    .filter((ratePlan) => selected.includes(ratePlan.id))
                    .map((ratePlan) => ratePlan.name || ratePlan.id)
                    .join(', ')
                }
                onChange={(event) => {
                  const value = event.target.value;
                  onSelectRatePlans(room.id, typeof value === 'string' ? value.split(',') : value);
                }}
              >
                {ratePlanOptions.map((ratePlan) => (
                  <MenuItem key={ratePlan.id} value={ratePlan.id}>
                    <Checkbox checked={selectedRatePlanIds[room.id]?.includes(ratePlan.id) ?? false} />
                    {ratePlan.name || ratePlan.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )
        )}
      </Stack>
      <Divider />
    </Box>
  );
}
